import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDownIcon, ChevronRightIcon } from "../icons";
import {
  StatusBadge,
  StatusType,
  FeatureBadgeType,
  FEATURE_BADGE_LABELS,
  getFeatureBadgeTooltipSuffix,
} from "../common/StatusBadge";

export type { FeatureBadgeType } from "../common/StatusBadge";

interface MenuItemProps {
  item: {
    id: string;
    label: string;
    path: string;
    icon: React.ComponentType<{ className?: string }>;
    exact?: boolean;
    badge?: FeatureBadgeType;
    /** Optional data-tour attribute for onboarding tour targeting */
    dataTour?: string;
    dataTestId?: string;
    submenu?: Array<{
      label: string;
      path: string;
      badgeCount?: number;
      /** Optional data-tour attribute for onboarding tour targeting */
      dataTour?: string;
      dataTestId?: string;
    }>;
  };
  collapsed: boolean;
  isActive: (path: string) => boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ item, collapsed, isActive }) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    // Rozwiń automatycznie jeśli któryś z podmenu jest aktywny
    if (item.submenu) {
      return item.submenu.some((subItem) => isActive(subItem.path));
    }
    return false;
  });
  const location = useLocation();

  const hasSubmenu = item.submenu && item.submenu.length > 0;
  const itemIsActive = isActive(item.path);
  const hasActiveSubmenu =
    hasSubmenu && item.submenu!.some((subItem) => isActive(subItem.path));

  const handleClick = () => {
    if (hasSubmenu && !collapsed) {
      setIsExpanded(true);
    }
  };

  const IconComponent = item.icon;

  if (collapsed) {
    return (
      <div className="relative group overflow-hidden">
        <Link
          to={hasSubmenu ? item.submenu![0].path : item.path}
          className={`flex items-center justify-center p-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            itemIsActive || hasActiveSubmenu
              ? "bg-primary-50 text-primary-700"
              : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
          }`}
          title={`${item.label}${getFeatureBadgeTooltipSuffix(item.badge)}`}
          aria-label={`${item.label}${getFeatureBadgeTooltipSuffix(item.badge)}`}
          {...(item.dataTour ? { "data-tour": item.dataTour } : {})}
        >
          <IconComponent className="h-5 w-5" />
        </Link>

        {/* Tooltip - uses aria-label from Link, no duplicate needed */}
      </div>
    );
  }

  return (
    <div data-testid={item.dataTestId}>
      {/* Main Menu Item */}
      <Link
        to={hasSubmenu ? item.submenu![0].path : item.path}
        onClick={handleClick}
        className={`flex items-center px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
          itemIsActive || hasActiveSubmenu
            ? "bg-primary-50 text-primary-700"
            : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
        }`}
        {...(item.dataTour ? { "data-tour": item.dataTour } : {})}
      >
        <IconComponent className="h-5 w-5 mr-3 flex-shrink-0" />
        <span className="flex-1 text-sm font-medium text-left">
          {item.label}
        </span>
        {item.badge && (
          <StatusBadge
            status={item.badge as StatusType}
            label={FEATURE_BADGE_LABELS[item.badge]}
            className="ml-2 text-[10px] px-1.5 py-0.5"
            data-testid={`menu-badge-${item.id}`}
          />
        )}
        {hasSubmenu && (
          <div className="ml-2 flex-shrink-0">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </div>
        )}
      </Link>

      {/* Submenu */}
      {hasSubmenu && isExpanded && (
        <div className="ml-8 mt-1 space-y-1">
          {item.submenu!.map((subItem) => {
            const subItemUrl = new URL(subItem.path, window.location.origin);
            const currentUrl = new URL(
              location.pathname + location.search,
              window.location.origin
            );
            const isSubItemActive =
              currentUrl.pathname === subItemUrl.pathname &&
              Array.from(subItemUrl.searchParams.entries()).every(
                ([key, val]) =>
                  currentUrl.searchParams.getAll(key).includes(val)
              );

            return (
              <Link
                key={subItem.path}
                to={subItem.path}
                aria-current={isSubItemActive ? "page" : undefined}
                data-testid={subItem.dataTestId}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                  isSubItemActive
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
                }`}
                {...(subItem.dataTour ? { "data-tour": subItem.dataTour } : {})}
              >
                <span>{subItem.label}</span>
                {subItem.badgeCount !== undefined && subItem.badgeCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 min-w-[1.25rem]">
                    {subItem.badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MenuItem;
